import { useState } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';
import * as XLSX from 'xlsx';

export default function KeywordMulti() {
  const checkAuth = useAuthGuard();
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!checkAuth()) return;
    const keywords = inputText.split('\n').map(k => k.trim()).filter(k => k);
    if (!keywords.length) return setError('키워드를 입력해주세요');
    if (keywords.length > 50) return setError('최대 50개까지 가능합니다');
    setLoading(true); setError('');
    try {
      const res = await client.post('/keyword/stats', { keywords });
      const inputSet = new Set(keywords.map(k => k.trim().toLowerCase().replace(/\s+/g, '')));
      const filtered = res.data.keywords.filter((r: any) => inputSet.has(r.keyword.toLowerCase().replace(/\s+/g, '')));
      // 중복 키워드 제거
      const seen = new Set<string>();
      const deduped = filtered.filter((r: any) => {
        const key = r.keyword.toLowerCase().replace(/\s+/g, '');
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setResults(deduped.sort((a: any, b: any) => b.total - a.total));
    } catch (e: any) {
      setError(e.response?.data?.detail || '조회 실패');
    } finally { setLoading(false); }
  };

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target?.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const keywords = rows.map(r => String(r[0] || '').trim()).filter(Boolean);
        if (keywords.length > 50) {
          setError(`최대 50개까지 가능합니다. (현재 ${keywords.length}개)`);
          return;
        }
        setInputText(keywords.join('\n'));
        setError('');
      } catch { setError('엑셀 파일 읽기 실패'); }
    };
    reader.readAsBinaryString(file);
    e.target.value = '';
  };

  const handleExcel = () => {
    if (!results.length) return;
    const data = results.map(r => ({
      '키워드': r.keyword,
      'PC 검색량': r.pc,
      '모바일 검색량': r.mobile,
      '총 검색량': r.total,
      '모바일 비율(%)': r.mob_ratio,
      '경쟁도': r.competition,
      'PC 클릭수': r.pc_click,
      '모바일 클릭수': r.mob_click,
      '총 클릭수': r.total_click,
      'PC CTR(%)': r.pc_ctr,
      '모바일 CTR(%)': r.mob_ctr,
      '검색량 대비 클릭률(%)': r.total_ctr,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '키워드검색량');
    XLSX.writeFile(wb, `키워드검색량_다중_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' } as any,
    btnGreen: { background: '#1A3A2A', border: '0.5px solid #2A5A3A', borderRadius: 8, padding: '10px 20px', color: '#00D4AA', fontSize: 13, fontWeight: 600, cursor: 'pointer' } as any,
    th: { color: '#4A5568', fontSize: 11 } as any,
  };

  const maxTotal = results.length > 0 ? Math.max(...results.map(r => r.total)) : 1;

  const compColor = (c: string) => c === '높음' ? '#F56565' : c === '중간' ? '#F6AD55' : '#00D4AA';

  return (
    <Layout>
      <div>
        <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>키워드 검색량 (다중)</h2>
        <p style={{ color: '#8892A4', fontSize: 12, margin: '0 0 20px' }}>최대 50개 키워드의 월간 검색량을 한번에 조회하세요</p>

        <div style={s.card}>
          <label style={{ display: 'block', color: '#8892A4', fontSize: 12, marginBottom: 6 }}>키워드 입력 (줄바꿈으로 구분, 최대 50개)</label>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <label style={{ background: '#1A2035', border: '0.5px solid #2A3045', borderRadius: 6, padding: '5px 12px', color: '#8892A4', fontSize: 12, cursor: 'pointer' }}>
              📂 엑셀 업로드
              <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleExcelUpload} />
            </label>
          </div>
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            placeholder={'마라톤양말\n러닝양말\n런닝양말\n...'}
            rows={8}
            style={{ width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 12px', color: '#E8ECF4', fontSize: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ color: '#4A5568', fontSize: 12, marginTop: 4, marginBottom: 12 }}>
            {inputText.split('\n').filter(k => k.trim()).length} / 50개
          </div>
          {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.btn} onClick={handleSearch} disabled={loading}>{loading ? '조회 중...' : '검색량 조회'}</button>
            {results.length > 0 && <button style={s.btnGreen} onClick={handleExcel}>📥 엑셀 다운로드</button>}
          </div>
        </div>

        {results.length > 0 && (
          <div style={{ ...s.card, overflowX: 'auto' }}>
            <div style={{ color: '#8892A4', fontSize: 12, marginBottom: 12 }}>총 {results.length}개 키워드</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ borderBottom: '0.5px solid #2A3045' }}>
                  {['키워드','PC','모바일','총 검색량','모바일%','경쟁도','PC클릭','M클릭','총클릭','PC CTR','M CTR','클릭률'].map(h => (
                    <th key={h} style={{ ...s.th, padding: '8px 6px', textAlign: h === '키워드' ? 'left' : 'right', fontWeight: 400 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid #1A2035' }}>
                    <td style={{ padding: '8px 6px', color: i === 0 ? '#00D4AA' : '#E8ECF4', fontSize: 13, fontWeight: i === 0 ? 700 : 400 }}>{r.keyword}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.pc.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.mobile.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', minWidth: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 8, background: '#0F1117', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ width: `${(r.total / maxTotal) * 100}%`, height: '100%', background: i === 0 ? '#E53E3E' : '#3182CE', borderRadius: 2 }} />
                        </div>
                        <span style={{ color: '#E8ECF4', fontSize: 12, width: 70, textAlign: 'right', flexShrink: 0 }}>{r.total.toLocaleString()}</span>
                      </div>
                    </td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.mob_ratio}%</td>
                    <td style={{ padding: '8px 6px', color: compColor(r.competition), fontSize: 11, textAlign: 'right' }}>{r.competition}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.pc_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.mob_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#E8ECF4', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>{r.total_click.toLocaleString()}</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.pc_ctr}%</td>
                    <td style={{ padding: '8px 6px', color: '#8892A4', fontSize: 12, textAlign: 'right' }}>{r.mob_ctr}%</td>
                    <td style={{ padding: '8px 6px', color: '#00D4AA', fontSize: 12, textAlign: 'right', fontWeight: 600 }}>{r.total_ctr}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
